/**
 * Environment Variables Configuration
 * Centralized management of all environment variables
 */

// Helper to safely get environment variables
// Set isOptional to true for features that aren't critical
const getEnvVar = (key, defaultValue = '', isOptional = false) => {
  const value = process.env[key];
  if (value === undefined && defaultValue === '' && !isOptional) {
    console.warn(`Environment variable ${key} is not set`);
  }
  return value || defaultValue;
};

// Environment detection
const NODE_ENV = process.env.NODE_ENV || 'development';
const isDevelopment = NODE_ENV === 'development';
const isProduction = NODE_ENV === 'production';
const isTest = NODE_ENV === 'test';

// API URL determination logic removed as we now use Supabase directly

// Site URL determination
const SITE_URL = isProduction
  ? (getEnvVar('REACT_APP_API_URL_MAIN') || 'https://bll-pro.vercel.app')
  : 'http://localhost:3000';

export const env = {
  // Node environment
  NODE_ENV,
  isDevelopment,
  isProduction,
  isTest,

  // API URLs
  SITE_URL,
  REDIRECT_URL: `${SITE_URL}/auth/callback`,

  // Supabase Configuration
  SUPABASE_URL: getEnvVar('REACT_APP_SUPABASE_URL', 'https://qfsklxqozaynbhyhwuha.supabase.co'),
  SUPABASE_ANON_KEY: getEnvVar('REACT_APP_SUPABASE_ANON_KEY', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFmc2tseHFvemF5bmJoeWh3dWhhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ0NzIwNzEsImV4cCI6MjA2MDA0ODA3MX0.s08JaOrnqNA7KAzNjwh1ctfcl6OQZafg_0KgpF63a20'),

  // Notion Configuration (Optional - only needed if using Notion integration)
  NOTION_TOKEN: getEnvVar('REACT_APP_NOTION_TOKEN', '', true),
  NOTION_DATABASE_ID: getEnvVar('REACT_APP_NOTION_DATABASE_ID', '', true),

  // Google OAuth (Optional - only needed if using Google login)
  GOOGLE_CLIENT_ID: getEnvVar('REACT_APP_GOOGLE_CLIENT_ID', '', true),
  GOOGLE_CLIENT_SECRET: getEnvVar('REACT_APP_GOOGLE_CLIENT_SECRET', '', true),

  // Stripe Configuration (Optional - only needed for payments)
  STRIPE_PUBLIC_KEY: getEnvVar('REACT_APP_STRIPE_PUBLIC_KEY', '', true),
  STRIPE_SECRET_KEY: getEnvVar('REACT_APP_STRIPE_SECRET_KEY', '', true),

  // Analytics (Optional - only needed if analytics is enabled)
  GA_TRACKING_ID: getEnvVar('REACT_APP_GA_TRACKING_ID', '', true),
  MIXPANEL_TOKEN: getEnvVar('REACT_APP_MIXPANEL_TOKEN', '', true),

  // Sentry Error Tracking (Optional - only needed if error tracking is enabled)
  SENTRY_DSN: getEnvVar('REACT_APP_SENTRY_DSN', '', true),
  SENTRY_ENVIRONMENT: NODE_ENV,

  // Feature Flags from Environment
  ENABLE_DARK_MODE: getEnvVar('REACT_APP_ENABLE_DARK_MODE', 'false', true) === 'true',
  ENABLE_BETA_FEATURES: getEnvVar('REACT_APP_ENABLE_BETA_FEATURES', 'false', true) === 'true',
  ENABLE_ANALYTICS: getEnvVar('REACT_APP_ENABLE_ANALYTICS', 'false', true) === 'true',
  ENABLE_ERROR_TRACKING: getEnvVar('REACT_APP_ENABLE_ERROR_TRACKING', 'false', true) === 'true',

  // App Version
  APP_VERSION: getEnvVar('REACT_APP_VERSION', '1.0.0', true),

  // Debug Mode
  DEBUG_MODE: getEnvVar('REACT_APP_DEBUG_MODE', isDevelopment ? 'true' : 'false', true) === 'true',

  // API Keys for External Services (Optional - only needed if using AI features)
  // AI keys removed to prevent accidental leakage as they are currently unused


  // Rate Limiting
  API_RATE_LIMIT: parseInt(getEnvVar('REACT_APP_API_RATE_LIMIT', '100'), 10),
  API_RATE_WINDOW_MS: parseInt(getEnvVar('REACT_APP_API_RATE_WINDOW_MS', '60000'), 10),

  // Cache Configuration
  CACHE_ENABLED: getEnvVar('REACT_APP_CACHE_ENABLED', 'true') === 'true',
  CACHE_TTL: parseInt(getEnvVar('REACT_APP_CACHE_TTL', '900'), 10), // 15 minutes

  // Session Configuration
  SESSION_TIMEOUT: parseInt(getEnvVar('REACT_APP_SESSION_TIMEOUT', '3600'), 10), // 1 hour
  SESSION_REFRESH_THRESHOLD: parseInt(getEnvVar('REACT_APP_SESSION_REFRESH_THRESHOLD', '300'), 10), // 5 minutes
};

// Validation function to check required environment variables
export const validateEnv = () => {
  const required = [
    'REACT_APP_SUPABASE_URL',
    'REACT_APP_SUPABASE_ANON_KEY',
  ];

  const missing = required.filter(key => !process.env[key]);

  if (missing.length > 0) {
    console.error('Missing required environment variables:', missing);
    if (isProduction) {
      throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
    }
  }

  return true;
};

// Export helper functions
export const getNotionToken = () => {
  const token = env.NOTION_TOKEN;
  if (!token) {
    console.error('❌ Notion token is not configured');
    throw new Error('Notion token is not configured');
  }
  return token;
};

export const getSupabaseConfig = () => ({
  url: env.SUPABASE_URL,
  anonKey: env.SUPABASE_ANON_KEY,
});

export const isDebugMode = () => env.DEBUG_MODE;

// Log environment info in development
if (isDevelopment && env.DEBUG_MODE) {
  // console.log removed
}